import { Component, Input, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { FlashpayAttachmentPayload, AdvancedFlashpayPayload } from 'features/transactions/transaction.model';
import { isValidEmail, joinEmails, splitEmails } from 'shared/utils/email.util';

/** One invoice the user ticked in Collect Payment. */
export interface AdvancedFlashpaySendInvoice {
  invoiceRef: string;
  dueDate?: Date | string | null;
  amount: number;
}

export interface AdvancedFlashpaySendData {
  customerName: string;
  contactEmail?: string | null;
  invoices: AdvancedFlashpaySendInvoice[];
}

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const DEFAULT_DAYS = 10;

/** YYYY-MM-DD in the browser's own day. */
function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Advanced FlashPay send dialog, opened from Collect Payment once the A/R user
 * has ticked the invoices: who gets the email, until when the link works, an
 * optional note and up to five PDF/image attachments. The parent's confirm
 * button reads {@link payload}.
 */
@Component({
  selector: 'app-advanced-flashpay-send-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CurrencyPipe,
    DatePipe,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './advanced-flashpay-send-dialog.component.html',
  styles: [
    `
      .afp-send {
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .afp-send__intro {
        margin: 0;
        color: #4b5563;
      }
      .afp-send table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
      }
      .afp-send th,
      .afp-send td {
        padding: 6px 8px;
        border-bottom: 1px solid #e5e7eb;
        text-align: left;
      }
      .afp-send .num {
        text-align: right;
      }
      .afp-send tfoot td {
        font-weight: 600;
        border-bottom: none;
      }
      .afp-send__invoices {
        max-height: 220px;
        overflow-y: auto;
      }
      .afp-send mat-form-field {
        width: 100%;
      }
      .afp-send__row {
        display: flex;
        gap: 12px;
      }
      .afp-send__row > * {
        flex: 1;
      }
      .afp-send__files {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .afp-send__file {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.9rem;
      }
      .afp-send__error {
        color: #b91c1c;
        font-size: 0.85rem;
      }
      .afp-send__hint {
        color: #6b7280;
        font-size: 0.85rem;
      }
      @media (max-width: 600px) {
        .afp-send__row {
          flex-direction: column;
        }
      }
    `,
  ],
})
export class AdvancedFlashpaySendDialogComponent implements OnInit {
  /** Modal wrapper data: `{ data: AdvancedFlashpaySendData }`. */
  @Input() data: any;

  readonly separatorKeys = [ENTER, COMMA, SEMICOLON] as const;
  readonly minDate = isoDay(new Date(Date.now() + 24 * 60 * 60 * 1000));

  recipients: string[] = [];
  attachments: (FlashpayAttachmentPayload & { sizeBytes: number })[] = [];
  fileError: string | null = null;
  recipientError: string | null = null;

  readonly form = new FormGroup({
    expiresDate: new FormControl(isoDay(new Date(Date.now() + DEFAULT_DAYS * 24 * 60 * 60 * 1000)), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    message: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(1000)] }),
  });

  get send(): AdvancedFlashpaySendData {
    return (this.data?.data ?? this.data ?? { customerName: '', invoices: [] }) as AdvancedFlashpaySendData;
  }

  get total(): number {
    return Math.round(this.send.invoices.reduce((sum, i) => sum + Math.round(Number(i.amount) * 100), 0)) / 100;
  }

  ngOnInit(): void {
    this.recipients = splitEmails(this.send.contactEmail);
  }

  addRecipient(event: MatChipInputEvent): void {
    const values = splitEmails(event.value);
    const invalid = values.filter((v) => !isValidEmail(v));
    for (const v of values.filter((x) => isValidEmail(x))) {
      if (!this.recipients.some((r) => r.toLowerCase() === v.toLowerCase())) {
        this.recipients.push(v);
      }
    }
    this.recipientError = invalid.length ? `Not a valid email address: ${invalid.join(', ')}` : null;
    event.chipInput?.clear();
  }

  removeRecipient(email: string): void {
    this.recipients = this.recipients.filter((r) => r !== email);
  }

  async onFiles(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.fileError = null;
    for (const file of files) {
      if (this.attachments.length >= MAX_ATTACHMENTS) {
        this.fileError = `At most ${MAX_ATTACHMENTS} files can be attached.`;
        break;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        this.fileError = `${file.name}: only PDF, PNG and JPEG files can be attached.`;
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.fileError = `${file.name} is larger than 2 MB.`;
        continue;
      }
      this.attachments.push({
        filename: file.name,
        mimeType: file.type,
        base64: await this.readBase64(file),
        sizeBytes: file.size,
      });
    }
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  /** Why the dialog cannot send yet, or null. */
  get problem(): string | null {
    if (this.send.invoices.length === 0) return 'Select at least one invoice.';
    if (this.recipients.length === 0) return 'Add at least one recipient email address.';
    if (this.form.invalid) return 'Choose the date the link expires.';
    if (this.form.controls.expiresDate.value < this.minDate) return 'The link must stay valid past today.';
    return null;
  }

  /** The request body, or null when something is missing (touches the form so errors show). */
  payload(): AdvancedFlashpayPayload | null {
    this.form.markAllAsTouched();
    if (this.problem) return null;
    const { expiresDate, message } = this.form.getRawValue();
    const body: AdvancedFlashpayPayload = {
      invoiceRefs: this.send.invoices.map((i) => i.invoiceRef),
      recipientEmails: joinEmails(this.recipients),
      expiresDate,
    };
    if (message.trim()) body.message = message.trim();
    if (this.attachments.length) {
      body.attachments = this.attachments.map(({ filename, mimeType, base64 }) => ({ filename, mimeType, base64 }));
    }
    return body;
  }

  private readBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
